import { useMemo } from 'react';
import { useInfiniteQuery as useInfiniteQueryReact } from 'react-query';

import { GetInfiniteQueryResult, IInfiniteQueryOptions } from 'core/types';

const DEFAULT_PAGE_SIZE = 100;

export const useInfiniteQuery = <T>(
  fetchData = null,
  queryKey,
  requestPayload?,
  options: IInfiniteQueryOptions<T> = {}
): GetInfiniteQueryResult<T> => {
  const enabled = options.enabled ?? true;
  const onSuccess = options.onSuccess ?? null;
  const fetchAllPages = options.fetchAllPages ?? false;

  const pageSize = requestPayload?.pageSize || DEFAULT_PAGE_SIZE;

  const fetchDataOptions = options?.mapResult ? { mapResult: options.mapResult } : {};

  const onSuccessHandler = (data) => {
    const allRecords = data.pages.reduce((acc, page) => ({ ...acc, ...page.records }));
    const pageData = data.pages.slice(-1)?.[0];

    if (onSuccess) {
      onSuccess(allRecords, pageData);
    }

    if (!fetchAllPages) {
      return;
    }

    const lastPageHasMore = data.pages[data.pages.length - 1]?.hasMore;
    const totalRecordsCount = data.pages[0].count;
    const fetchedRecordsCount = data.pages.reduce((acc, page) => acc + page.records.length, 0);

    if (lastPageHasMore || fetchedRecordsCount < totalRecordsCount) {
      fetchNextPage();
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetched,
    isFetching,
    isError,
    refetch,
    error,
  } = useInfiniteQueryReact(
    [queryKey, requestPayload],
    ({ pageParam: page = 1, signal }) =>
      fetchData
        ? fetchData({ ...requestPayload, page, pageSize }, signal, fetchDataOptions)
        : { records: [], count: 0 },
    {
      getNextPageParam: (lastPage, page) => {
        if (options?.getNextPageParam) {
          return options.getNextPageParam(lastPage, page);
        }

        // If the server provides hasMore, use it directly
        if (lastPage.hasMore !== undefined) {
          return lastPage.hasMore ? page.length + 1 : undefined;
        }

        // If count is undefined (not provided by the server) we determine if there are more pages
        // based on the last page length of the records and the page size
        if (!lastPage.count && lastPage.records.length < pageSize) {
          return undefined;
        }

        if (page.length >= lastPage.count / pageSize) {
          return undefined;
        }

        return page.length + 1;
      },
      onSuccess: onSuccessHandler,
      enabled,
      // Only when the caller says: react-query's default (3 retries) stands for every other query.
      ...(options.retry !== undefined && { retry: options.retry }),
    }
  );

  const records = useMemo(() => data?.pages?.flatMap((page) => page.records) ?? [], [data]);
  const pageRecords = useMemo(() => data?.pages?.[data?.pages?.length - 1]?.records ?? [], [data]);
  const extraData = useMemo(() => data?.pages?.flatMap((page) => page?.extraData) ?? [], [data]);
  const currentPage = data?.pages?.length;
  const totals = data?.pages?.[0]?.totals;
  const count = data?.pages?.[0]?.count;

  const totalPagesCount = Math.ceil(count / pageSize) || 0;

  let loadingPercentage = totalPagesCount > 0 ? (currentPage / totalPagesCount) * 100 : 0;

  if (
    count === 0 ||
    (count === undefined && data?.pages?.[data?.pages?.length - 1]?.hasMore === false)
  ) {
    loadingPercentage = 100;
  }

  return {
    records,
    pageRecords,
    extraData,
    isLoading,
    isFetched,
    isFetching,
    isFetchingNextPage,
    isError,
    statusCode: (error as any)?.response?.status ?? null,
    errorMessage: (error as any)?.response?.data?.error ?? null,
    hasNextPage,
    fetchNextPage,
    refetch,
    totals,
    count,
    currentPage,
    totalPagesCount,
    loadingPercentage,
  };
};
